import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl, useLocation, history } from 'umi';
import { useMount } from 'ahooks';
import classNames from 'classnames';
import QAInput from '../components/QAInput';
import type { IQAInputRef } from '../components/QAInput/types';
import DSAgentWrapper, { ISelectedDsModelType } from '@/pages/aigc/pages/Agent/Home/components/DSAgent';
import ChatSwitchBtn from '@/pages/aigc/pages/Agent/Home/components/ChatSwitchBtn';
import Recommend from './components/Recommend';
import UseCase from './components/UseCase';
import { buriedBtnPoint, buriedPointPage } from '@/pages/home/utils/buriedPoint';
import type { ISelectedSkillTagData } from '../components/QAInput/SkillMention/SelectedSkillTag';
import { ChatTypes } from '@/pages/aigc/pages/Agent/ChatPage/types/chat';
import { storage } from '@/utils/utils';
import useAISmartTableData from './hooks/useAISmartTableData';
import { useUrlQueryParam } from '@/pages/aigc/hooks/useQuery';
import { useAgentModelsData } from '@/pages/aigc/pages/Agent/AgentCollection/hooks';
import useHomeDefaultAgentFromChatList from './hooks/useHomeDefaultAgentFromChatList';
import { DATABRAIN_AGENT_ID, DATABRAIN_AGENT_ID2 } from '@/pages/aigc/pages/Agent/config/constant';
import titleZH from '../svg/main-title-zh.svg';
import titleEN from '../svg/main-title-en.svg';
import subTitleZH from '../svg/subTitleZH.svg';
import subTitleEN from '../svg/subTitleEN.svg';
import './index.scss';
import OrganizationPermission, { OrganizationText } from '@/pages/aigc/components/OrganizationPermission';
import PosterTemplateGallery from './components/PosterTemplateGallery';
import { IPosterTemplate } from './components/PosterTemplateGallery/types';
import { useLocalState } from '../ChatPage/hooks/useLocalState';
import aiMagicPosterIcon from '@/pages/aigc/assets/svg/icon-magical-image-reproduction.svg';
import aiMagicPosterIconDefault from '@/pages/aigc/assets/svg/icon-magical-image-reproduction-default.svg';
import { usePosterTemplateData } from './hooks/usePosterTemplateData';
import useDSAgentData from './hooks/useDSAgentData';
import agentHomeTableIcon from '@/pages/aigc/assets/svg/agent-home-table-icon.svg';
import agentHomeTableIconDefault from '@/pages/aigc/assets/svg/agent-home-table-icon-default.svg';
import BasicImage from '@home/components/BasicImage';
import { useExternalQueryConfig } from '../Copilot/hooks/useExternalQueryConfig';
import { useSkillsData } from '../SkillsCollection/hooks';
import { buildSerializedSkillInlineLabel } from '../components/QAInput/utils/inlineMentionLabel';
import { normalizeSerializedMentionGaps } from '../components/QAInput/mentionEditor/mentionTokenDom';
import useHomeUrlKbPrefill from './hooks/useHomeUrlKbPrefill';
import MorePlatformBtn from './components/MorePlatformBtn';
import agentHomeAiChat from '@/pages/aigc/assets/svg/agent-home-ai-chat.svg';
import agentHomeAiChatActive from '@/pages/aigc/assets/svg/agent-home-ai-chat-active.svg';
import agentHomeDsChat from '@/pages/aigc/assets/svg/agent-home-ds-chat.svg';
import agentHomeDsChatActive from '@/pages/aigc/assets/svg/agent-home-ds-chat-active.svg';

// ChatTypes 对应的图标映射
// const CHAT_TYPE_ICON_MAP: Record<ChatTypes, string> = {
//   [ChatTypes.AI_CHAT]: agentHomeAiChat,
//   [ChatTypes.DS_AGENT_CHAT]: agentHomeDsChat,
//   [ChatTypes.MAGIC_POSTER]: '',
//   [ChatTypes.AI_TABLE_CHAT]: '',
// };

// ChatTypes 对应的图片 src 映射 - default state
const CHAT_TYPE_ICON_SRC_MAP: Partial<Record<ChatTypes, string>> = {
  [ChatTypes.MAGIC_POSTER]: aiMagicPosterIconDefault,
  [ChatTypes.AI_TABLE_CHAT]: agentHomeTableIconDefault,
  [ChatTypes.AI_CHAT]: agentHomeAiChat,
  [ChatTypes.DS_AGENT_CHAT]: agentHomeDsChat,
};

// ChatTypes 对应的图片 src 映射 - active (highlighted) state
const CHAT_TYPE_ACTIVE_ICON_SRC_MAP: Partial<Record<ChatTypes, string>> = {
  [ChatTypes.MAGIC_POSTER]: aiMagicPosterIcon,
  [ChatTypes.AI_TABLE_CHAT]: agentHomeTableIcon,
  [ChatTypes.AI_CHAT]: agentHomeAiChatActive,
  [ChatTypes.DS_AGENT_CHAT]: agentHomeDsChatActive,
};

// ChatTypes 对应的国际化 key 映射
const CHAT_TYPE_I18N_MAP: Record<ChatTypes, string> = {
  [ChatTypes.AI_CHAT]: 'dba_chat_type_ai_agent',
  [ChatTypes.DS_AGENT_CHAT]: 'dba_ds_model_btn_prephrase',
  [ChatTypes.MAGIC_POSTER]: 'dba_chat_type_magic_poster',
  [ChatTypes.AI_TABLE_CHAT]: 'dba_chat_type_ai_table',
};

// ChatTypes 对应的埋点信息映射
const CHAT_TYPE_BURIED_MSG_MAP: Record<ChatTypes, string> = {
  [ChatTypes.AI_CHAT]: '智能问答',
  [ChatTypes.DS_AGENT_CHAT]: 'Data Science专业报告生成',
  [ChatTypes.AI_TABLE_CHAT]: 'AI智能表格',
  [ChatTypes.MAGIC_POSTER]: '数据魔法海报',
};

// ChatTypes 对应的 serverParams function.name 映射
const CHAT_TYPE_FUNCTION_NAME_MAP: Partial<Record<ChatTypes, string>> = {
  [ChatTypes.AI_CHAT]: 'smart_qa',
  [ChatTypes.AI_TABLE_CHAT]: 'ai_excel',
  [ChatTypes.MAGIC_POSTER]: 'magic_poster',
};

const AgentLayout = () => {
  const { locale, formatMessage } = useIntl();  
  const location = useLocation<{
    selectedSkill?: ISelectedSkillTagData;
    initialQuestion?: string;
  }>();
  const { query } = location as any;
  const { tab, from: urlFrom } = query || {};
  // gallery 来源是「会话级一次性意图」，挂载时捕获一次，之后只由「切换 tab」主动关闭。
  // 不能用 `urlFrom === 'gallery'` 响应式派生：line-103 effect 会在 URL 缺 tab 时抹掉 from，
  // 导致 isFromGallery 翻 false → gallery skill 替换 effect 早返 → 占位 tag 的 skillId 永远为空 →
  // SkillMention 无法按 skillId 过滤掉已选 skill（外部顶部导航入口 `/agent/home?from=gallery` 无 tab，必现）。
  // 用 state（而非 URL 派生）还能保证：切 tab 时显式置 false，关闭 gallery 文案/技能预填，让输入框回到空态。
  const [isFromGallery, setIsFromGallery] = useState(() => urlFrom === 'gallery');
  const isEn = locale === 'en-US';
  const [activeTab, setActiveTab] = useLocalState<string>('__dba_copilot_tab__', '');
  // 清除全局会话模式状态，防止 skill 模式残留影响其他页签
  const [, setSessionTemplateMode] = useLocalState<string>('__dba_session_template_mode__', '');
  const initSelectedSample = storage.getItem('__dba_ds_selected_sample') as ISelectedDsModelType;
  // 校验 URL tab 参数是否为已有的 ChatTypes 值，命中则使用，否则默认 AI_CHAT
  const initialTab = activeTab || (Object.values(ChatTypes).includes(tab) ? tab : ChatTypes.AI_CHAT);
  const [{ tab: currentSelectedChat = initialTab }, setCurrentSelectedChat] = useUrlQueryParam(['tab']);

  // activeTab 有缓存值时，同步到 URL query 参数
  // 仅当 URL 中没有 tab 参数且 activeTab 有值时才同步，避免重复进入页面时清空 URL tab
  // 显式带 from: '' 防御 useUrlQueryParam 幽灵缓存把 from=gallery 写回 URL（cleanObject 会把空值删除）
  // 新增对话后清除 template 参数，防止 skill 等模板模式残留
  useEffect(() => {
    if (!tab) {
      (setCurrentSelectedChat as (params: Record<string, unknown>) => void)({
        tab: activeTab || ChatTypes.AI_CHAT,
        from: '',
        kbId: '',
        agentId: '',
        template: '', // 清除 template 参数，防止 skill 模式残留
      });
    }
  }, [activeTab, tab]);

  // 用于和 DSAgentWrapper 联动，实现输入框文案显示
  const [currentSelectedDsModel, setCurrentSelectedDsModel] = useState<ISelectedDsModelType | undefined>(initSelectedSample ?? undefined);
  // 在父级请求 DS Agent 数据
  const { dSExpertAgent, loading: dsAgentLoading } = useDSAgentData();
  // 海报灵感模板数据
  const { data: posterTemplates, loading: posterTemplatesLoading } = usePosterTemplateData();
  // 选中的海报模板（做同款）。'prefix-only' 表示模板名已删、仅保留前缀"以"
  const [selectedPosterTemplate, setSelectedPosterTemplate] = useState<IPosterTemplate | 'prefix-only' | null>(null);

  // 魔法海报 taggingNode
  // key 绑定模板 ID，确保切换模板时 React 完整卸载/挂载标签节点，避免文本不更新
  const posterTaggingNode = useMemo(() => {
    if (currentSelectedChat !== ChatTypes.MAGIC_POSTER || !selectedPosterTemplate) return undefined;
    if (selectedPosterTemplate === 'prefix-only') {
      return (
        <span key="prefix-only" className='dba-poster-tag-wrapper'>
          <span className="dba-poster-tag-prefix">{isEn ? 'Based on' : '以'}</span>
        </span>
      );
    }
    return (
      <span key={selectedPosterTemplate.templateId} className='dba-poster-tag-wrapper'>
        <span className="dba-poster-tag-prefix">{isEn ? 'Based on' : '以'}</span>
        <span className="dba-poster-tag">
          <BasicImage src={(isEn ? selectedPosterTemplate.coverUrl_en : selectedPosterTemplate.coverUrl_cn) || ''}  alt={isEn ? selectedPosterTemplate.titleEn : selectedPosterTemplate.titleCn} className="dba-poster-tag-icon" />
          {isEn ? selectedPosterTemplate.titleEn : selectedPosterTemplate.titleCn}
        </span>
      </span>
    );
  }, [currentSelectedChat, selectedPosterTemplate, isEn]);
  // 实际模板对象（排除 'prefix-only'），用于 serverParams 等需要访问属性的场景
  const actualPosterTemplate = selectedPosterTemplate && selectedPosterTemplate !== 'prefix-only' ? selectedPosterTemplate : null;

  // 清除标签回调：根据当前页签执行不同逻辑
  const handleTaggingNodeClear = () => {
    if (currentSelectedChat === ChatTypes.MAGIC_POSTER) {
      // 魔法海报：两步删除 → 有模板→仅前缀→null
      if (selectedPosterTemplate && selectedPosterTemplate !== 'prefix-only') {
        setSelectedPosterTemplate('prefix-only');
      } else {
        setSelectedPosterTemplate(null);
      }
    } else if (currentSelectedChat === ChatTypes.DS_AGENT_CHAT) {
      // DS 报告：清除已选模型，同时清除 storage 缓存防止重新加载后恢复默认值
      setCurrentSelectedDsModel(undefined);
      storage.removeItem('__dba_ds_selected_sample');
    }
  };

  // 请求 AI 智能表格推荐问题数据
  const { aiTableRecommendData, loading: aiTableLoading } = useAISmartTableData();
  // GameWiki `?kbId=`：拉列表+文档后预填 @（如「库名 4篇」），无效/无权限静默跳过
  const {
    selectedKnowledge,
    setSelectedKnowledge,
    isKbPrefillPending,
    kbPrefillSerialized,
    urlForcedAgentId,
    clearKbPrefill,
  } = useHomeUrlKbPrefill();
  const { selectedAgentId } = useAgentModelsData();
  useHomeDefaultAgentFromChatList(
    currentSelectedChat === ChatTypes.AI_CHAT,
    isFromGallery ? DATABRAIN_AGENT_ID2 : urlForcedAgentId,
  );
  const shouldHideByTechlandAgent = selectedAgentId === DATABRAIN_AGENT_ID;

  // 外部配置读取 & QAInput props 计算（清空 store，仅第一个页签生效）
  const { externalQueryConfig, externalQueryInputProps } = useExternalQueryConfig({ clearStore: true, activeTab: currentSelectedChat });
  const { allSkills, loading: skillsLoading } = useSkillsData(isEn);

  // 从 Skills 详情联动案例等跳转时预填提问文案（需在 selectedSkill 消费前读取）
  const [initialQuestion] = useState<string>(() => {
    if (isFromGallery) {
      return '';
    }
    const question = (location as any).state?.initialQuestion as string | undefined;
    return question?.trim() || '';
  });

  // 从 Skills 集合页面 / gallery 跳转过来时，读取选中的 Skill 并预填
  const [initialSelectedSkills, setInitialSelectedSkills] = useState<ISelectedSkillTagData[]>(() => {
    // Gallery 来源：不预选 skill tag，由 useHomeDefaultAgentFromChatList 统一处理 Agent 选中
    // （旧逻辑：loading 占位 + 接口返回后替换，已废弃）
    // if (isFromGallery) {
    //   return [{
    //     skillId: '', name: '', display_name_cn: '', display_name_en: '',
    //     icon: '', displayName: '-', isLoading: true,
    //   }];
    // }

    const skillData = (location as any).state?.selectedSkill as ISelectedSkillTagData | undefined;
    if (skillData?.skillId && skillData?.name) {
      // 用完即清除 state，避免刷新页面重复填充
      history.replace({ pathname: location.pathname, search: location.search });
      return [skillData];
    }
    return [];
  });

  // gallery：接口返回后写入真实 skill，并消费 URL 中的 from=gallery
  useEffect(() => {
    if (!isFromGallery || skillsLoading) {
      return;
    }

    // setInitialSelectedSkills(
    //   galleryMatchedSkill?.length
    //     ? galleryMatchedSkill.map((item) => buildSelectedSkillTag(item, isEn))
    //     : [],
    // );

    const params = new URLSearchParams(window.location.search);
    if (params.has('from')) {
      params.delete('from');
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [isFromGallery, skillsLoading,, isEn]);

  // gallery：接口返回与 effect 写 state 差一帧；state 尚无真实 skill 时同帧读 galleryMatchedSkill
  const resolvedSelectedSkills = useMemo((): ISelectedSkillTagData[] => {
    if (!isFromGallery || skillsLoading) {
      return initialSelectedSkills;
    }
    const hasRealSkill = initialSelectedSkills.some(
      (s) => s.skillId && !s.isLoading,
    );
    if (hasRealSkill) {
      return initialSelectedSkills;
    }
    return [];
  }, [isFromGallery, skillsLoading, isEn, initialSelectedSkills]);

  // 筛选有 diy_example 的 skill，用于 gallery 默认文案
  const galleryDiySkill = useMemo(() => {
    if (!isFromGallery || skillsLoading || !allSkills.length) return undefined;
    return allSkills.filter((item: any) => Boolean(item.diy_example?.trim() || item.diy_example_en?.trim()));
  }, [isFromGallery, skillsLoading, allSkills]);

  // 取第一个有 diy_example 的 skill 作为默认提问文案
  const galleryDefaultValue = useMemo(() => {
    if (!isFromGallery) {
      return '';
    }
    // 接口未返回前不注入占位文案，避免输入框短暂出现 "-"
    if (skillsLoading) {
      return '-';
    }
    const diySkill = (galleryDiySkill as any[])?.[0];
    if (diySkill) {
      const text = isEn
        ? (diySkill.diy_example_en?.trim() || diySkill.diy_example?.trim())
        : (diySkill.diy_example?.trim() || diySkill.diy_example_en?.trim());
      if (text) return text;
    }
    return formatMessage({ id: 'dba_gallery_default_text' });
  }, [isFromGallery, skillsLoading, galleryDiySkill, isEn, formatMessage]);

  /** 预填正文：URL kb @ + skill 引用串 + 可选尾部文案 */
  const qaInputInitialValue = useMemo(() => {
    const skillPrefix = resolvedSelectedSkills
      .filter((skill) => !skill.isLoading && skill.skillId && skill.name)
      .map((skill) => buildSerializedSkillInlineLabel(skill, isEn))
      .join('');
    const tail = isFromGallery ? galleryDefaultValue : initialQuestion;

    if (kbPrefillSerialized !== undefined) {
      if (!kbPrefillSerialized && !skillPrefix && !tail) {
        return '';
      }
      return normalizeSerializedMentionGaps(`${kbPrefillSerialized}${skillPrefix}${tail || ''}`);
    }

    if (!skillPrefix && !tail) {
      return undefined;
    }
    return normalizeSerializedMentionGaps(`${skillPrefix}${tail || ''}`);
  }, [
    kbPrefillSerialized,
    resolvedSelectedSkills,
    isEn,
    isFromGallery,
    galleryDefaultValue,
    initialQuestion,
  ]);

  const qaInputRef = useRef<IQAInputRef>(null);

  useMount(() => {
    buriedPointPage({
      pageId: '700401',
      pageName: 'agentHome',
      extInfo: 'agentHome',
      pagePath: '/agent/home',
      typeId: 'aigc',
    });
  });

  return (
    <section className="dba-agent-home">
      <div className={classNames('dba-home-container', {
        'dba-home-ai-table-container': currentSelectedChat === ChatTypes.AI_TABLE_CHAT,
        'dba-home-magic-poster-container': currentSelectedChat === ChatTypes.MAGIC_POSTER,
        'dba-home-ds-agent-container': currentSelectedChat === ChatTypes.DS_AGENT_CHAT,
        'dba-home-ai-chat-container': currentSelectedChat === ChatTypes.AI_CHAT,
      })}>
        <MorePlatformBtn />
        <div className="dba-home-title">
          <div className="dba-agent-header">
            <div className="dba-big-title">
              <img src={isEn ? titleEN : titleZH} className="dba-main-title-image" />
              <OrganizationPermission 
                fallback={<OrganizationText textKey="agentHomeSybTitle"  className='dba-big-sub-title'/>} 
                additionalHideCondition={shouldHideByTechlandAgent}
              >
                <div className="dba-subtitle">
                  <img
                    src={isEn ? subTitleEN : subTitleZH}
                    className={classNames(
                      'dba-agent-icon',
                      isEn && 'dba-icon-en',
                    )}
                  />
                </div>
              </OrganizationPermission>
            </div>
          </div>
          {/* 对话种类切换 */}
          <div className='dba-chat-switcher'>
            {Object.keys(ChatTypes).map((key) => {
              const chatType = ChatTypes[key as keyof typeof ChatTypes];
              return (
                <ChatSwitchBtn 
                  key={key} 
                  // svgIcon={CHAT_TYPE_ICON_MAP[chatType]} 
                  iconSrc={CHAT_TYPE_ICON_SRC_MAP[chatType]}
                  activeIconSrc={CHAT_TYPE_ACTIVE_ICON_SRC_MAP[chatType]}
                  isActive={currentSelectedChat === chatType}
                  onClick={() => {
                    // 同页签重复点击不做状态重置，避免误清空已选 Skill
                    if (chatType === currentSelectedChat) return;
                    // 单次 URL 更新：切换 tab 并清除 from=gallery，避免其它 tab 仍走 gallery 预填逻辑
                    // 非 AI_CHAT 页签清除 template 参数，防止 skill 模式残留影响其他页签
                    (setCurrentSelectedChat as (params: Record<string, unknown>) => void)({
                      tab: chatType,
                      from: '',
                      kbId: '',
                      agentId: '',
                      ...(chatType !== ChatTypes.AI_CHAT ? { template: '' } : {}),
                    });
                    setActiveTab(chatType);
                    // 切 tab 关闭 gallery 预填：停止 gallery 文案/技能注入，输入框回到空态
                    setIsFromGallery(false);
                    clearKbPrefill();
                    // 切换页签时清空从 Skills 页面带过来的预填数据
                    if (initialSelectedSkills.length > 0) {
                      setInitialSelectedSkills([]);
                    }
                    // 切换 tab 时重置魔法海报选中的模板
                    setSelectedPosterTemplate(null);
                    // 非 AI_CHAT 页签清除全局会话模式状态，防止 skill 模式残留
                    if (chatType !== ChatTypes.AI_CHAT) {
                      setSessionTemplateMode('');
                    }
                    buriedBtnPoint({
                      pageId: '700501',
                      buttonId: '700501015',
                      extInfo: JSON.stringify({
                        message: CHAT_TYPE_BURIED_MSG_MAP[chatType],
                      }),
                      typeId: 'aigc',
                    });
                    if(chatType === ChatTypes.DS_AGENT_CHAT && !storage.getItem('__dba_ds_show_new'))
                      storage.setItem("__dba_ds_show_new", true);
                  }}
                  showBeta={[ChatTypes.AI_TABLE_CHAT, ChatTypes.MAGIC_POSTER].includes(chatType)}
                >
                  {formatMessage?.({ id: CHAT_TYPE_I18N_MAP[chatType] })}
                </ChatSwitchBtn>
              );
            })}
          </div>

          <QAInput
            ref={qaInputRef}
            key={currentSelectedChat}
            className={`dba-home-input ${(currentSelectedChat === ChatTypes.DS_AGENT_CHAT || currentSelectedChat === ChatTypes.MAGIC_POSTER) && 'dba-home-input-ds-agent'}`}  
            from='agentHome'
            isPosterMode={currentSelectedChat === ChatTypes.MAGIC_POSTER}
            selectedPosterTemplate={actualPosterTemplate}
            taggingNode={
              currentSelectedChat === ChatTypes.DS_AGENT_CHAT
                ? (currentSelectedDsModel ? (
                  <span className='dba-ds-agent-tag-wrapper'>
                    <span className="dba-ds-agent-tag">{formatMessage({ id: 'dba_ds_model_btn_prephrase' })}{currentSelectedDsModel?.name ? ` – ${currentSelectedDsModel.name}` : ''}</span>
                  </span>
                ) : (
                  // DS 页签占位：用于识别 DS 模式并拦截发送，但不禁用输入
                  <div style={{ display: 'none' }} aria-hidden />
                ))
                : posterTaggingNode
            }
            currentSelectedDsModel={currentSelectedDsModel}
            serverParams={CHAT_TYPE_FUNCTION_NAME_MAP[currentSelectedChat as ChatTypes] ? {
              function: {
                name: CHAT_TYPE_FUNCTION_NAME_MAP[currentSelectedChat as ChatTypes],
                source: 'main_chat',
                ...currentSelectedChat === ChatTypes.MAGIC_POSTER ? {
                  args: {
                    content_prompt: isEn ? actualPosterTemplate?.content_prompt_en : actualPosterTemplate?.content_prompt_cn,
                    poster_prompt: isEn ? actualPosterTemplate?.poster_prompt_en : actualPosterTemplate?.poster_prompt_cn,
                    // 0 - 默认(default)：用户没选模板
                    // 1 - 预设(preset)：用户选了预设模版（首页入口仅支持 0/1）
                    poster_template: actualPosterTemplate ? 1 : 0,
                  },
                } : {}
              },
              // 魔法海报时，传递最小必要元数据用于 Question 组件显示风格引用；
              // 修复：与 externalQueryConfig 合并 frontend_metadata，避免互相覆盖导致 type 等关键字段丢失
              ...(currentSelectedChat === ChatTypes.MAGIC_POSTER || externalQueryConfig ? {
                frontend_metadata: {
                  ...(currentSelectedChat === ChatTypes.MAGIC_POSTER ? {
                    type: 'magic_poster',
                    poster_template: actualPosterTemplate ? 1 : 0,
                    // 预设模板：只传 templateId，由 FieldRefTag 组件动态查询模板数据和生成国际化文案
                    ...(actualPosterTemplate ? { templateId: actualPosterTemplate.templateId } : {}),
                  } : {}),
                  ...(externalQueryConfig ? {
                    field_name: externalQueryConfig.prefixTag,
                    type: externalQueryConfig.form,
                  } : {}),
                },
                ...(externalQueryConfig?.prefixTag ? {
                  quoted_content: { type: 'game', value: externalQueryConfig.prefixTag },
                } : {}),
              } : {}),
            } : undefined}
            disabled={isFromGallery && skillsLoading}
            selectedKnowledge={selectedKnowledge}
            onSelectedKnowledgeChange={setSelectedKnowledge}
            selectedSkills={resolvedSelectedSkills.length > 0 ? resolvedSelectedSkills : undefined}
            onSelectedSkillsChange={setInitialSelectedSkills}
            onTaggingNodeClear={handleTaggingNodeClear}
            {...externalQueryInputProps}
            {...(qaInputInitialValue !== undefined && { value: qaInputInitialValue })}
            sendDisabled={isKbPrefillPending}

          />
        </div>

        {(currentSelectedChat === ChatTypes.AI_CHAT) && (
          <>
            <Recommend
              className="dba-home-recom"
              qaInputRef={qaInputRef}
            />
            <OrganizationPermission> <UseCase /> </OrganizationPermission>
          </>
        )}
        {(currentSelectedChat === ChatTypes.AI_TABLE_CHAT) && (
          <>
            <Recommend
              className="dba-home-recom"
              dataSource={aiTableRecommendData}
              dataLoading={aiTableLoading}
              showTitle={true}
              template="aiExcel"
              qaInputRef={qaInputRef}
            />
          </>
        )}
        {currentSelectedChat === ChatTypes.DS_AGENT_CHAT && <DSAgentWrapper 
          currentSelectedDsModel={currentSelectedDsModel}
          setCurrentSelectedDsModel={setCurrentSelectedDsModel}
          dSExpertAgent={dSExpertAgent}
          loading={dsAgentLoading}
        />}
        {currentSelectedChat === ChatTypes.MAGIC_POSTER && (
          <PosterTemplateGallery
            templates={posterTemplates?.[0]?.list}
            loading={posterTemplatesLoading}
            onMakeSame={(template) => {
              setSelectedPosterTemplate(template);
            }}
          />
        )}
      </div>
    </section>
  );
};
export default AgentLayout;